import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePacientes } from '../hooks/usePacientes';
import PantallaCargaMedica from '../components/common/PantallaCargaMedica';
import TablaPacientes from '../components/pacientes/TablaPacientes';
import ExpedienteDetallado from '../components/ExpedienteDetallado';
import NuevoPaciente from '../components/NuevoPaciente';
import NuevaConsulta from '../components/NuevaConsulta';
import ModalNuevaFactura from '../components/facturacion/ModalNuevaFactura';
import ModalImportarPacientes from '../components/pacientes/ModalImportarPacientes';
import { useAuth } from '../context/AuthContext';
import { Search as SearchIcon } from '@mui/icons-material';

export default function Pacientes() {
    const { id: urlId } = useParams();
    const navigate = useNavigate();

    // 1. CONSUMO DE LÓGICA DE NEGOCIO (Aislada por SRP)
    const { pacientes, total, page, setPage, search, setSearch, loading, recargar } = usePacientes();

    // 2. ESTADOS EXCLUSIVOS DE CONTROL DE FLUJO DE INTERFAZ
    const [openNuevoPaciente, setOpenNuevoPaciente] = useState(false);
    const [pacienteIdConsulta, setPacienteIdConsulta] = useState(null);
    const [pacienteFactura, setPacienteFactura] = useState(null);
    // Importar pacientes de otro programa: solo dirección
    const { user } = useAuth();
    const puedeImportar = ['admin', 'supermedico'].includes(user?.role || user?.rol);
    const [importando, setImportando] = useState(false);

    const handleBack = useCallback(() => navigate('/pacientes'), [navigate]);

    // 🛡️ ESCUDO ANTI-PARPADEO: Si hay ID, renderiza directo el Expediente sin montar la lista
    if (urlId) {
        return <ExpedienteDetallado pacienteId={urlId} onBack={handleBack} />;
    }
// Solo mostramos esta pantalla gigante si está cargando y es la primera vez (no hay pacientes aún)
    if (loading && pacientes.length === 0) {
        return (
            <div className="p-6 md:p-8 max-w-7xl mx-auto">
                <PantallaCargaMedica variante="pacientes" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 space-y-6 max-w-7xl mx-auto animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">Pacientes</h1>
                    <p className="text-sm font-semibold text-slate-500 mt-1">Fichas e historias clínicas de la clínica</p>
                </div>
                <div className="flex flex-wrap gap-3">
                    {puedeImportar && (
                        <button onClick={() => setImportando(true)} className="px-5 py-3.5 bg-white text-slate-700 font-bold text-sm rounded-2xl border border-slate-200 shadow-sm hover:bg-slate-50 transition-all">
                            Importar desde Excel
                        </button>
                    )}
                    <button onClick={() => setOpenNuevoPaciente(true)} className="px-6 py-3.5 bg-slate-900 text-white font-bold text-sm rounded-2xl shadow-md hover:bg-slate-800 transition-all flex items-center gap-2">
                        + Alta de Paciente
                    </button>
                </div>
            </div>

            {/* Buscador minimalista */}
            <div className="relative group max-w-md">
                <SearchIcon fontSize="small" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-clinica-primary pointer-events-none" />
                <input
                    aria-label="Buscar pacientes"
                    type="text"
                    placeholder="Buscar por nombre, apellidos o DNI…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full py-3.5 pl-12 pr-4 bg-white border border-slate-200/80 rounded-2xl text-sm font-medium focus:outline-none focus:border-clinica-primary focus:ring-4 focus:ring-clinica-primary/10 transition-all shadow-sm"
                />
            </div>

            {/* 🚀 Renderizado de la tabla delegada */}
            <TablaPacientes
                pacientes={pacientes}
                loading={loading}
                onVerExpediente={(id) => navigate(`/pacientes/${id}`)}
                onNuevaConsulta={(id) => setPacienteIdConsulta(id)}
                onNuevaFactura={(p) => setPacienteFactura(p)}
            />

            {/* 📦 Paginador */}
            <div className="flex items-center justify-between px-2 pt-2">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Página <span className="text-slate-900">{page + 1}</span> de <span className="text-slate-900">{Math.ceil(total / 25) || 1}</span> ({total} pacientes)</span>
                <div className="flex gap-2">
                    <button disabled={page === 0} onClick={() => setPage(page - 1)} className="px-4 py-2 text-xs font-black uppercase tracking-wide text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-50 shadow-sm transition-all">Anterior</button>
                    <button disabled={(page + 1) * 25 >= total} onClick={() => setPage(page + 1)} className="px-4 py-2 text-xs font-black uppercase tracking-wide text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-50 shadow-sm transition-all">Siguiente</button>
                </div>
            </div>

            {/* =========================================================
                📦 CAPA DE MODALES (Aislados de la renderización del censo)
               ========================================================= */}
            {importando && (
                <ModalImportarPacientes onClose={() => setImportando(false)} onTerminado={recargar} />
            )}

            {openNuevoPaciente && (
                <NuevoPaciente open={openNuevoPaciente} onClose={() => setOpenNuevoPaciente(false)} onSuccess={(nuevoId) => { recargar(); if(nuevoId) navigate(`/pacientes/${nuevoId}`); }} />
            )}

            {pacienteIdConsulta && (
                <NuevaConsulta open={Boolean(pacienteIdConsulta)} onClose={() => setPacienteIdConsulta(null)} pacienteId={pacienteIdConsulta} onSuccess={recargar} />
            )}

            {pacienteFactura && (
                <ModalNuevaFactura open={Boolean(pacienteFactura)} onClose={() => setPacienteFactura(null)} paciente={pacienteFactura} onSuccess={recargar} />
            )}
        </div>
    );
}